import type { TFunction } from 'i18next';
import { SessionType, type InterventionTemplateResponse } from '../../api/generated';
import type { TableColumn } from '../UI';
import { formatFlexCompetences, formatRequiredCompetences } from '../../services/interventionTemplateMappers';
import { InterventionRowActions } from './InterventionRowActions';
import {
  INTERVENTION_DURATION_COLUMN_KEY,
  INTERVENTION_NAME_COLUMN_KEY,
  ACTIONS_COLUMN_WIDTH,
  ONLINE_ROOM_NAME
} from './interventionsConstants';
import styles from './InterventionsList.module.css';

// One entry per session type instead of a chain of ternaries. Adding a type to
// the API becomes a compile error here rather than a silent fall-through that
// labels the new kind "individual", which is what the ternaries did.
const SESSION_TYPE_BADGE_CLASSES: Record<SessionType, string> = {
  [SessionType.Group]: styles.badgeGroup,
  [SessionType.Internal]: styles.badgeInternal,
  [SessionType.Individual]: styles.badgeIndividual
};

// Read by translationKeys.test.ts, which resolves `t(SOME_MAP[...])` as well as
// inline literals, so these stay guarded against going missing from either
// language.
const SESSION_TYPE_LABEL_KEYS: Record<SessionType, string> = {
  [SessionType.Group]: 'config-iv-group',
  [SessionType.Internal]: 'config-iv-internal',
  [SessionType.Individual]: 'config-iv-individual'
};

interface InterventionColumnCallbacks {
  onEdit: (intervention: InterventionTemplateResponse) => void;
  onDuplicate: (intervention: InterventionTemplateResponse) => void;
  onDelete: (intervention: InterventionTemplateResponse) => void;
  // Competence ids resolve to profession names for the skills column.
  professionNameById: Map<string, string>;
}

export const getInterventionColumns = (
  t: TFunction,
  { onEdit, onDuplicate, onDelete, professionNameById }: InterventionColumnCallbacks
): TableColumn<InterventionTemplateResponse>[] => [
  {
    key: INTERVENTION_NAME_COLUMN_KEY,
    header: t('config-iv-col-name'),
    sortable: true,
    // Sorts on the name alone: the cell also renders the colour bar, which
    // carries no order.
    sortValue: (iv: InterventionTemplateResponse) => iv.name,
    render: (iv: InterventionTemplateResponse) => (
      <div className={styles.ivNameCell}>
        <span className={styles.ivColorBar} style={{ backgroundColor: iv.color }} />
        {iv.name}
      </div>
    )
  },
  {
    key: INTERVENTION_DURATION_COLUMN_KEY,
    header: t('config-iv-col-duration'),
    width: 120,
    sortable: true,
    // The raw minutes rather than the rendered "45 min". The comparator's
    // numeric option would order the labels correctly too, but only by parsing
    // digits out of a translated string; sorting the number the cell was built
    // from does not depend on what the unit suffix looks like in any language.
    sortValue: (iv: InterventionTemplateResponse) => iv.durationMinutes,
    render: (iv: InterventionTemplateResponse) =>
      `${iv.durationMinutes} ${t('room-booking-minutes-suffix')}`
  },
  {
    key: 'sessionType',
    header: t('config-iv-col-session-type'),
    width: 140,
    render: (iv: InterventionTemplateResponse) => (
      <span className={`${styles.badge} ${SESSION_TYPE_BADGE_CLASSES[iv.sessionType]}`}>
        {t(SESSION_TYPE_LABEL_KEYS[iv.sessionType])}
      </span>
    )
  },
  {
    key: 'rooms',
    header: t('config-iv-col-rooms'),
    render: (iv: InterventionTemplateResponse) => (
      <div className={styles.roomList}>
        {iv.roomRequirements.map((r) => (
          <span
            key={r.id}
            className={`${styles.roomTag} ${r.name === ONLINE_ROOM_NAME ? styles.roomTagOnline : ''}`}
          >
            {r.name}
          </span>
        ))}
      </div>
    )
  },
  {
    key: 'skills',
    header: t('config-iv-col-skills'),
    render: (iv: InterventionTemplateResponse) => {
      const flexSummary = formatFlexCompetences(iv, professionNameById, t);
      return (
        <div className={styles.skillsCell}>
          <span>{formatRequiredCompetences(iv, professionNameById, t)}</span>
          {flexSummary && <span className={styles.flexLine}>{flexSummary}</span>}
        </div>
      );
    }
  },
  {
    key: 'actions',
    header: '',
    width: ACTIONS_COLUMN_WIDTH,
    headerAlign: 'center',
    render: (iv: InterventionTemplateResponse) => (
      <InterventionRowActions
        interventionName={iv.name}
        onEdit={() => onEdit(iv)}
        onDuplicate={() => onDuplicate(iv)}
        onDelete={() => onDelete(iv)}
      />
    )
  }
];
